import { router } from "@inertiajs/react";
import { Button, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import type { DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import { SignatureTable } from "../../components/signature/SignatureTable";
import { showDeleteModal } from "../../helpers/modal";
import { getFullName } from "../../helpers/stringHelper";
import { useYearContext } from "../../hooks/useYearContext";
import { ContentShell } from "../../layout/ContentShell";
import type { Pagination } from "../../models/pagination";
import type { Signature } from "../../models/signature";
import { edit_signature_path, new_signature_path, signature_path, signatures_path } from "../../routes";

type SignaturesProps = {
  signatures: Signature[];
  filter: {
    sort_key?: "first_name" | "last_name";
    sort_direction?: "asc" | "desc";
  };
  pagination: Pagination;
};

export default function Signatures({ signatures, filter, pagination }: SignaturesProps) {
  const sortStatus: DataTableSortStatus<Signature> = {
    columnAccessor: filter?.sort_key || "",
    direction: filter?.sort_direction || "asc",
  };

  const { pathWithYearContext } = useYearContext();
  const [isFetching, setIsFetching] = useState(false);

  const onSortChange = (sortStatus: DataTableSortStatus<Signature>) => handlePageUpdate(sortStatus, pagination.page);
  const onPageChange = (page: number) => handlePageUpdate(sortStatus, page);

  const handlePageUpdate = (sortStatus: DataTableSortStatus<Signature>, page: number) => {
    setIsFetching(true);
    router.get(
      pathWithYearContext(signatures_path),
      {
        filter: {
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page,
      },
      {
        onFinish: () => setIsFetching(false),
      },
    );
  };

  const onDelete = (signature: Signature) =>
    showDeleteModal({
      title: `Unterschrift von ${getFullName(signature)}`,
      article: "die",
      onConfirm: () => router.delete(pathWithYearContext(signature_path, signature.id)),
      additionalText: <Text>Stellen Sie sicher, dass diese Unterschrift in keinen Berichten hinterlegt ist!</Text>,
    });

  const onEdit = (signature: Signature) => router.get(pathWithYearContext(edit_signature_path, signature.id));
  const onNew = () => router.get(pathWithYearContext(new_signature_path));

  return (
    <ContentShell
      title="Unterschriften"
      actions={
        <Button leftSection={<IconPlus />} variant="light" onClick={onNew}>
          Neu
        </Button>
      }
    >
      <SignatureTable
        signatures={signatures}
        sortStatus={sortStatus}
        onEdit={onEdit}
        onDelete={onDelete}
        onSortStatusChange={onSortChange}
        pagination={pagination}
        onPageChange={onPageChange}
        fetching={isFetching}
      />
    </ContentShell>
  );
}
